import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiFile, BiDownload, BiChevronRight } from 'react-icons/bi';
import AlertModal from '@components/modal/AlertModal';
import TranslatableText from '@components/translations/TranslatableText';
import { useGetReportTemplatesQuery } from '@features/project/projectApiSlice';
import type { ReportTemplate } from '@features/project/projectTypes';
import type { TranslationKey } from '@typedefs/modulesInterfaces';
import ReportAssumptionsEditor from './ReportAssumptionsEditor';
import './NarrativeReportModal.css';

// Keeps the FAO report reachable if /report-templates/ fails.
const FALLBACK_TEMPLATES: ReportTemplate[] = [{ id: 'fao', label: 'EX-ACT report', languages: [] }];
// Backend limit on the `content` HTML.
const MAX_CONTENT_LENGTH = 20000;

// Frontend copy for the templates we know; unknown ones fall back to the backend label.
const TEMPLATE_COPY: Record<string, { label: TranslationKey; description: TranslationKey; asksAssumptions?: boolean }> = {
  fao: { label: 'project.report_template_fao', description: 'project.report_template_fao_description' },
  ifad: { label: 'project.report_template_ifad', description: 'project.report_template_ifad_description', asksAssumptions: true },
};

interface NarrativeReportModalProps {
  open: boolean;
  onClose: () => void;
  onDownload: (template: ReportTemplate, content?: string) => void;
}

const NarrativeReportModal = ({ open, onClose, onDownload }: NarrativeReportModalProps) => {
  const { t } = useTranslation();
  const { data: templates = FALLBACK_TEMPLATES } = useGetReportTemplatesQuery();
  // The template waiting for assumptions; null while choosing a template.
  const [assumptionsFor, setAssumptionsFor] = useState<ReportTemplate | null>(null);
  // Kept after submit so the user doesn't retype it if the download is blocked (readiness, errors).
  const [content, setContent] = useState('');
  const [hasText, setHasText] = useState(false);
  const [contentError, setContentError] = useState('');

  const close = () => {
    setAssumptionsFor(null);
    setContentError('');
    onClose();
  };

  const download = (template: ReportTemplate, html?: string) => {
    close();
    onDownload(template, html);
  };

  const choose = (template: ReportTemplate) => {
    if (TEMPLATE_COPY[template.id]?.asksAssumptions) {
      setAssumptionsFor(template);
    } else {
      download(template);
    }
  };

  const generate = () => {
    if (!assumptionsFor) return;
    // Quill 2.0.3's semantic HTML turns every space into &nbsp;, which would stop the PDF from wrapping lines.
    const html = content.replaceAll('&nbsp;', ' ');
    if (html.length > MAX_CONTENT_LENGTH) {
      setContentError(t('project.report_assumptions_too_long', { max: MAX_CONTENT_LENGTH }));
      return;
    }
    // The assumptions are optional: an editor with no text sends nothing.
    download(assumptionsFor, hasText ? html : undefined);
  };

  const templateLabel = (template: ReportTemplate) => {
    const copy = TEMPLATE_COPY[template.id];
    return copy ? t(copy.label) : template.label;
  };

  if (assumptionsFor) {
    return (
      <AlertModal
        modalOpen={open}
        handleCancel={close}
        headerText={TEMPLATE_COPY[assumptionsFor.id]?.label ?? 'main.download_narrative_report'}
        size="auto"
        buttons={[
          {
            children: <TranslatableText translationKey="main.back" />,
            onClick: () => { setAssumptionsFor(null); setContentError(''); },
            type: 'button',
          },
          {
            children: <TranslatableText translationKey="project.generate_report" />,
            onClick: generate,
            className: 'narrative-report-primary',
            type: 'button',
          },
        ]}
      >
        <div className="narrative-report-modal d-flex f-column gap-8">
          <div>
            <TranslatableText className="narrative-report-heading" translationKey="project.report_assumptions_prompt" />
            <TranslatableText className="narrative-report-hint" translationKey="project.report_assumptions_hint" />
          </div>
          <ReportAssumptionsEditor
            value={content}
            onChange={(html, editorHasText) => { setContent(html); setHasText(editorHasText); setContentError(''); }}
          />
          {contentError && <div className="text-error fs-13" role="alert">{contentError}</div>}
        </div>
      </AlertModal>
    );
  }

  return (
    <AlertModal
      modalOpen={open}
      handleCancel={close}
      headerText="main.download_narrative_report"
      size="auto"
      buttons={[{ children: <TranslatableText translationKey="main.cancel" />, onClick: close, type: 'button' }]}
    >
      <div className="narrative-report-modal d-flex f-column gap-8">
        <TranslatableText className="narrative-report-hint" translationKey="project.report_type_message" />
        {templates.map(template => {
          const copy = TEMPLATE_COPY[template.id];
          const ActionIcon = copy?.asksAssumptions ? BiChevronRight : BiDownload;
          return (
            <button key={template.id} type="button" className="report-template-option" onClick={() => choose(template)}>
              <span className="report-template-option__icon" aria-hidden="true"><BiFile size={22} /></span>
              <span className="report-template-option__text">
                <span className="report-template-option__title">
                  {templateLabel(template)}
                  {template.languages.map(language => (
                    <span key={language} className="report-template-option__language">{language}</span>
                  ))}
                </span>
                {copy && <TranslatableText className="report-template-option__description" translationKey={copy.description} />}
              </span>
              <ActionIcon className="report-template-option__action" size={22} aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </AlertModal>
  );
};

export default NarrativeReportModal;
